import { ENTITY_API_URL } from "../environment";
import { initAxiosFor } from "../security/axiosUtils";

export enum TenantLifecycleStatus {
  Active = "Active",
  Lead = "Lead",
  Qualified = "Qualified",
  Negotiation = "Negotiation",
  Contracted = "Contracted",
  Onboarding = "Onboarding",
  Paused = "Paused",
  Churning = "Churning",
  Churned = "Churned",
  Deleted = "Deleted"
}

// Mapping from backend numeric values to string enum (må matche rekkefølgen i C# enum)
const TenantLifecycleStatusFromNumber: TenantLifecycleStatus[] = [
  TenantLifecycleStatus.Active,    // 0
  TenantLifecycleStatus.Lead,      // 1
  TenantLifecycleStatus.Qualified, // 2
  TenantLifecycleStatus.Negotiation, // 3
  TenantLifecycleStatus.Contracted,  // 4
  TenantLifecycleStatus.Onboarding,  // 5
  TenantLifecycleStatus.Paused,      // 6
  TenantLifecycleStatus.Churning,    // 7
  TenantLifecycleStatus.Churned,     // 8
  TenantLifecycleStatus.Deleted,     // 9
];

// Mapping from string to numeric value for API calls
const TenantLifecycleStatusToNumber: Record<TenantLifecycleStatus, number> = {
  [TenantLifecycleStatus.Active]: 0,
  [TenantLifecycleStatus.Lead]: 1,
  [TenantLifecycleStatus.Qualified]: 2,
  [TenantLifecycleStatus.Negotiation]: 3,
  [TenantLifecycleStatus.Contracted]: 4,
  [TenantLifecycleStatus.Onboarding]: 5,
  [TenantLifecycleStatus.Paused]: 6,
  [TenantLifecycleStatus.Churning]: 7,
  [TenantLifecycleStatus.Churned]: 8,
  [TenantLifecycleStatus.Deleted]: 9,
};

export function toTenantLifecycleStatus(value: number | string | undefined): TenantLifecycleStatus {
  if (value === undefined || value === null) return TenantLifecycleStatus.Active;
  if (typeof value === 'number') {
    return TenantLifecycleStatusFromNumber[value] ?? TenantLifecycleStatus.Active;
  }
  return value as TenantLifecycleStatus;
}

export function tenantLifecycleStatusToNumber(status: TenantLifecycleStatus): number {
  return TenantLifecycleStatusToNumber[status];
}

export enum SiteLifecycleStatus {
  Active = "Active",
  Planned = "Planned",
  Installing = "Installing",
  Commissioning = "Commissioning",
  Maintenance = "Maintenance",
  Paused = "Paused",
  Decommissioned = "Decommissioned",
  Deleted = "Deleted"
}

// Mapping from backend numeric values to string enum (må matche rekkefølgen i C# enum)
const SiteLifecycleStatusFromNumber: SiteLifecycleStatus[] = [
  SiteLifecycleStatus.Active,        // 0
  SiteLifecycleStatus.Planned,       // 1
  SiteLifecycleStatus.Installing,    // 2
  SiteLifecycleStatus.Commissioning, // 3
  SiteLifecycleStatus.Maintenance,   // 4
  SiteLifecycleStatus.Paused,        // 5
  SiteLifecycleStatus.Decommissioned, // 6
  SiteLifecycleStatus.Deleted,       // 7
];

// Mapping from string to numeric value for API calls
const SiteLifecycleStatusToNumber: Record<SiteLifecycleStatus, number> = {
  [SiteLifecycleStatus.Active]: 0,
  [SiteLifecycleStatus.Planned]: 1,
  [SiteLifecycleStatus.Installing]: 2,
  [SiteLifecycleStatus.Commissioning]: 3,
  [SiteLifecycleStatus.Maintenance]: 4,
  [SiteLifecycleStatus.Paused]: 5,
  [SiteLifecycleStatus.Decommissioned]: 6,
  [SiteLifecycleStatus.Deleted]: 7,
};

export function toSiteLifecycleStatus(value: number | string | undefined): SiteLifecycleStatus {
  if (value === undefined || value === null) return SiteLifecycleStatus.Active;
  if (typeof value === 'number') {
    return SiteLifecycleStatusFromNumber[value] ?? SiteLifecycleStatus.Active;
  }
  return value as SiteLifecycleStatus;
}

export function siteLifecycleStatusToNumber(status: SiteLifecycleStatus): number {
  return SiteLifecycleStatusToNumber[status];
}

export interface SiteSubscriptionPrices {
  site: number;
  entity: number;
}

export interface SiteSubscription {
  siteId: string;
  siteName: string;
  subscription: number;
  numberOfEntities: number;
  siteSubscriptionPrices: SiteSubscriptionPrices | null;
}

export interface TenantSubscription {
  tenantId: string;
  tenantName: string;
  sites: SiteSubscription[];
  totalSubscription: number;
  defaultSitePrice: number;
  defaultEntityPrice: number;
}

export interface SubscriptionsReport {
  tenantsSubscriptions: TenantSubscription[];
  sumTotal: number;
}

export function useTenantService() {
  const axiosInstance = initAxiosFor(ENTITY_API_URL);
  return {
    getAll: async (includeDeleted: boolean = false) => (await axiosInstance.get(`${ENTITY_API_URL}/api/tenants?includeDeleted=${includeDeleted}`))?.data,
    getOne: async (id: string) => (await axiosInstance.get(`${ENTITY_API_URL}/api/tenants/` + id))?.data,
    post: async (value: any, includeTypes: boolean) => (await axiosInstance.post(`${ENTITY_API_URL}/api/tenants`, value, { params: { includeTypes } }))?.data,
    put: async (id: string, value: any) => (await axiosInstance.put(`${ENTITY_API_URL}/api/tenants/` + id, value))?.data,
    getSubscriptionsReport: async (
      tenantStatuses?: TenantLifecycleStatus[],
      siteStatuses?: SiteLifecycleStatus[]
    ): Promise<SubscriptionsReport> => {
      const params = new URLSearchParams();
      // Send numeric values for enum parameters (ASP.NET Core default binding)
      tenantStatuses?.forEach(s => params.append('tenantStatuses', String(tenantLifecycleStatusToNumber(s))));
      siteStatuses?.forEach(s => params.append('siteStatuses', String(siteLifecycleStatusToNumber(s))));
      const queryString = params.toString();
      const url = queryString
        ? `${ENTITY_API_URL}/api/tenants/subscriptions?${queryString}`
        : `${ENTITY_API_URL}/api/tenants/subscriptions`;
      return (await axiosInstance.get(url))?.data;
    },
    postCommand: async (id: string, command: any) => (await axiosInstance.post(`${ENTITY_API_URL}/api/tenants/${id}/commands`, command))?.data,
  }
}
